import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft, LogOut, School as SchoolIcon, Users, BarChart3,
  TrendingUp, Activity, AlertCircle, ChevronUp, ChevronDown,
} from 'lucide-react';

const STATUS_PALETTE = {
  engaged: { label: 'Engaged', bg: 'bg-emerald-100', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  quiet:   { label: 'Quiet',   bg: 'bg-amber-100',   text: 'text-amber-700',   dot: 'bg-amber-500' },
  inactive:{ label: 'Inactive',bg: 'bg-rose-100',    text: 'text-rose-700',    dot: 'bg-rose-500' },
};

function relTime(iso) {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

export default function SchoolTeacherClassInsights() {
  const { classId } = useParams();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [school, setSchool] = useState(null);
  const [data, setData] = useState(null);
  const [activeSubject, setActiveSubject] = useState(null);
  const [windowSel, setWindowSel] = useState('week');
  const [sortKey, setSortKey] = useState('chats_week');
  const [sortDir, setSortDir] = useState('desc');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async (subj, win) => {
    setLoading(true);
    setError(null);
    try {
      const meRes = await api.get('/auth/me');
      setMe(meRes.data);
      const schoolPromise = meRes.data?.school_id
        ? api.get(`/schools/${meRes.data.school_id}`)
        : Promise.resolve({ data: null });
      const params = { window: win };
      if (subj && subj !== 'all') params.subject = subj;
      const [schoolRes, insRes] = await Promise.all([
        schoolPromise,
        api.get(`/teacher/me/classes/${classId}/insights`, { params }),
      ]);
      setSchool(schoolRes.data);
      setData(insRes.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Unable to load insights');
    } finally {
      setLoading(false);
    }
  }, [classId]);

  useEffect(() => { load(activeSubject, windowSel); }, [load, activeSubject, windowSel]);

  const handleLogout = async () => {
    try { await api.post('/auth/logout'); } catch { /* ignore */ }
    localStorage.removeItem('yukti_session_token');
    navigate(school?.slug ? `/school/${school.slug}/login` : '/', { replace: true });
  };

  const brandColor = school?.brand_color || '#4F46E5';
  const cls = data?.school_class;
  const kpis = data?.kpis || {};
  const allowedSubjects = useMemo(() => data?.allowed_subjects || [], [data]);

  const sortedRows = useMemo(() => {
    if (!data?.rows) return [];
    const arr = [...data.rows];
    arr.sort((a, b) => {
      let av = a[sortKey];
      let bv = b[sortKey];
      if (sortKey === 'last_active') {
        av = av ? new Date(av).getTime() : 0;
        bv = bv ? new Date(bv).getTime() : 0;
      }
      if (typeof av === 'string') av = av.toLowerCase();
      if (typeof bv === 'string') bv = bv.toLowerCase();
      if (av === bv) return 0;
      return sortDir === 'asc' ? (av > bv ? 1 : -1) : (av > bv ? -1 : 1);
    });
    return arr;
  }, [data, sortKey, sortDir]);

  const setSort = (key) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir(key === 'student_name' ? 'asc' : 'desc');
    }
  };

  const SortHead = ({ k, label, className = '' }) => (
    <th
      className={`px-3 py-2 text-left text-xs font-semibold text-slate-600 cursor-pointer select-none ${className}`}
      onClick={() => setSort(k)}
      data-testid={`insights-sort-${k}`}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {sortKey === k && (sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
      </span>
    </th>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50" data-testid="class-insights-page">
      <header
        className="border-b bg-white/90 backdrop-blur sticky top-0 z-10"
        style={{ borderTopWidth: 4, borderTopColor: brandColor }}
      >
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {school?.logo_url ? (
              <img src={school.logo_url} alt={school.name} className="w-10 h-10 rounded-lg object-contain bg-white border" />
            ) : (
              <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white" style={{ backgroundColor: brandColor }}>
                <SchoolIcon size={20} />
              </div>
            )}
            <div>
              <h1 className="font-bold text-slate-900">{school?.name || 'Your School'}</h1>
              <p className="text-xs text-slate-500">{me?.email} · Teacher</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={handleLogout}>
            <LogOut size={16} className="mr-1.5" /> Logout
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-6">
        <Button
          variant="ghost" size="sm"
          onClick={() => navigate('/school-teacher')}
          className="mb-3"
          data-testid="back-to-dashboard-btn"
        >
          <ArrowLeft size={14} className="mr-1.5" /> Back to dashboard
        </Button>

        {error ? (
          <div className="border border-red-200 bg-red-50 rounded-xl p-6 text-center" data-testid="insights-error">
            <p className="text-red-700 font-semibold">{error}</p>
          </div>
        ) : (
          <>
            {/* Tab strip — Student List · Insights */}
            <div className="border-b mb-4 flex items-center gap-1" data-testid="roster-insights-tab-strip">
              <button
                className="px-4 py-2 text-sm text-slate-500 hover:text-slate-700"
                onClick={() => navigate(`/school-teacher/class/${classId}`)}
                data-testid="tab-roster"
              >
                Student List
              </button>
              <button
                className="px-4 py-2 text-sm font-semibold border-b-2"
                style={{ borderColor: brandColor, color: brandColor }}
                data-testid="tab-insights"
              >
                Insights
              </button>
            </div>

            {/* Class header */}
            <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
              <div>
                <h2 className="text-2xl font-bold text-slate-900" data-testid="insights-class-name">
                  {cls?.name || 'Class'} Insights
                </h2>
                <p className="text-sm text-slate-600 mt-1">
                  Grade {cls?.grade}{cls?.section ? ` · Section ${cls.section}` : ''} · {cls?.academic_year}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <div className="bg-white border rounded-lg p-1 flex" data-testid="window-toggle">
                  <button
                    className={`px-3 py-1 text-xs rounded ${windowSel === 'week' ? 'bg-indigo-100 text-indigo-700 font-semibold' : 'text-slate-500'}`}
                    onClick={() => { setWindowSel('week'); setSortKey('chats_week'); }}
                    data-testid="window-toggle-week"
                  >
                    Week
                  </button>
                  <button
                    className={`px-3 py-1 text-xs rounded ${windowSel === 'month' ? 'bg-indigo-100 text-indigo-700 font-semibold' : 'text-slate-500'}`}
                    onClick={() => { setWindowSel('month'); setSortKey('chats_month'); }}
                    data-testid="window-toggle-month"
                  >
                    Month
                  </button>
                </div>
              </div>
            </div>

            {/* Subject filter */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <Badge
                onClick={() => setActiveSubject(null)}
                variant={!activeSubject ? 'default' : 'outline'}
                className="cursor-pointer"
                data-testid="subject-filter-chip-all"
              >
                All my subjects
              </Badge>
              {allowedSubjects.map((s) => (
                <Badge
                  key={s}
                  onClick={() => setActiveSubject(s)}
                  variant={activeSubject === s ? 'default' : 'outline'}
                  className="cursor-pointer"
                  data-testid={`subject-filter-chip-${s}`}
                >
                  {s}
                </Badge>
              ))}
            </div>

            {/* KPI tiles */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
              <KpiTile
                label="Active this week"
                value={`${kpis.active_students_this_week ?? 0} / ${kpis.total_students ?? 0}`}
                icon={<Activity size={16} className="text-emerald-500" />}
                testid="kpi-active-this-week"
              />
              <KpiTile
                label="Avg chats / student"
                value={kpis.avg_chats_per_student ?? 0}
                icon={<TrendingUp size={16} className="text-indigo-500" />}
                testid="kpi-avg-chats"
              />
              <KpiTile
                label="Inactive students"
                value={kpis.inactive_count ?? 0}
                icon={<AlertCircle size={16} className="text-rose-500" />}
                testid="kpi-inactive-count"
              />
              <KpiTile
                label="Total enrolled"
                value={kpis.total_students ?? 0}
                icon={<Users size={16} className="text-slate-500" />}
                testid="kpi-total-students"
              />
            </div>

            {/* Table */}
            {sortedRows.length === 0 ? (
              <div
                className="border-2 border-dashed rounded-xl py-12 text-center bg-white/60"
                data-testid="insights-empty-state"
              >
                <BarChart3 size={28} className="text-slate-300 mx-auto mb-2" />
                <p className="text-slate-700 font-semibold">
                  {(kpis.total_students ?? 0) === 0
                    ? 'No students enrolled in this class yet'
                    : 'No engagement data yet — students will appear here once they start chatting'}
                </p>
              </div>
            ) : (
              <div className="bg-white border rounded-xl overflow-hidden" data-testid="insights-table">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <SortHead k="student_name" label="Student" />
                      <SortHead k="last_active" label="Last active" />
                      <SortHead k="chats_week" label="Chats / week" className="text-right" />
                      <SortHead k="chats_month" label="Chats / month" className="text-right" />
                      <SortHead k="status" label="Status" />
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {sortedRows.map((r) => {
                      const palette = STATUS_PALETTE[r.status] || STATUS_PALETTE.inactive;
                      return (
                        <tr
                          key={r.enrollment_id}
                          onClick={() => navigate(`/school-teacher/students/${r.enrollment_id}/chats`)}
                          className="hover:bg-slate-50 cursor-pointer"
                          data-testid={`insights-row-${r.enrollment_id}`}
                        >
                          <td className="px-3 py-2.5">
                            <div className="flex items-center gap-2">
                              <div
                                className="w-8 h-8 rounded-full flex items-center justify-center text-white font-semibold text-xs"
                                style={{ backgroundColor: brandColor }}
                              >
                                {(r.student_name || '?').charAt(0).toUpperCase()}
                              </div>
                              <span className="font-medium text-slate-900 text-sm">{r.student_name}</span>
                            </div>
                          </td>
                          <td className="px-3 py-2.5 text-xs text-slate-600">{relTime(r.last_active)}</td>
                          <td className="px-3 py-2.5 text-right font-semibold text-sm text-slate-900">{r.chats_week}</td>
                          <td className="px-3 py-2.5 text-right text-sm text-slate-700">{r.chats_month}</td>
                          <td className="px-3 py-2.5">
                            <span
                              className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium ${palette.bg} ${palette.text}`}
                              data-testid={`insights-status-badge-${r.enrollment_id}`}
                            >
                              <span className={`w-1.5 h-1.5 rounded-full ${palette.dot}`} />
                              {palette.label}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <p className="text-xs text-slate-400 mt-4">
              Status thresholds: Engaged = 3+ chats/week · Quiet = 1-2 chats/week · Inactive = 0 chats/week
            </p>
          </>
        )}
      </main>
    </div>
  );
}

function KpiTile({ label, value, icon, testid }) {
  return (
    <div className="bg-white border rounded-xl p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
        {icon}
        {label}
      </div>
      <div className="text-2xl font-bold text-slate-900">{value}</div>
    </div>
  );
}
